import { c as _c } from "react/compiler-runtime";
import { PlusIcon } from '@systemeio/icons';
import React, { type JSX, type ReactNode } from 'react';
import { Button, IconTile, Loader, type IconTileProps } from 'shared/components';
export interface RuleCardProps {
  /** Icon rendered inside the card's heading tile. */
  icon: JSX.Element;
  /** Tone of the heading tile — accent blue for triggers, green for actions. */
  tone: IconTileProps['tone'];
  title: ReactNode;
  description: ReactNode;
  /** Label of both "add" affordances (heading icon button and empty-state box). */
  addLabel: string;
  /** Omitted when there is nothing left to add (e.g. every trigger is already used). */
  onAdd?: () => void;
  isAddDisabled?: boolean;
  isLoading?: boolean;
  /** True while the card has no trigger/action step yet — renders the empty-state box. */
  isEmpty: boolean;
  children: ReactNode;
  dataTest: string;
}

/**
 * One half of the rule editor: the "Trigger" or the "Action" column. Owns the card chrome,
 * the heading with its add-step affordances and the empty/loading states; the configured
 * steps themselves are rendered as `children`.
 */
export function RuleCard(t0) {
  const $ = _c(33);
  const {
    icon,
    tone,
    title,
    description,
    addLabel,
    onAdd,
    isAddDisabled,
    isLoading,
    isEmpty,
    children,
    dataTest
  } = t0;
  let t1;
  if ($[0] !== icon || $[1] !== tone) {
    t1 = <IconTile aria-hidden="true" tone={tone}>{icon}</IconTile>;
    $[0] = icon;
    $[1] = tone;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== title) {
    t2 = <span className="text-text-primary text-base font-medium">{title}</span>;
    $[3] = title;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== description) {
    t3 = <span className="text-text-tertiary text-sm">{description}</span>;
    $[5] = description;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = <div className="flex min-w-0 flex-col gap-1">{t2}{t3}</div>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t1 || $[11] !== t4) {
    t5 = <div className="flex min-w-0 items-center gap-3">{t1}{t4}</div>;
    $[10] = t1;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== addLabel || $[14] !== dataTest || $[15] !== isAddDisabled || $[16] !== onAdd) {
    t6 = onAdd && <Button aria-label={addLabel} data-test={`${dataTest}-add`} disabled={isAddDisabled} onClick={onAdd} size="icon-md" variant="tertiary-gray"><PlusIcon /></Button>;
    $[13] = addLabel;
    $[14] = dataTest;
    $[15] = isAddDisabled;
    $[16] = onAdd;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t5 || $[19] !== t6) {
    t7 = <div className="flex items-center justify-between gap-3">{t5}{t6}</div>;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== addLabel || $[22] !== children || $[23] !== dataTest || $[24] !== isAddDisabled || $[25] !== isEmpty || $[26] !== isLoading || $[27] !== onAdd) {
    t8 = isLoading ? <div className="flex min-h-56 items-center justify-center"><Loader className="scale-150" /></div> : isEmpty ? onAdd && <button className="border-border-200 bg-bg-primary hover:border-border-accent active:border-border-accent active:bg-bg-secondary focus-visible:focus-ring-primary transition-base tablet:min-h-61 desktop:min-h-56 flex min-h-56 w-full flex-col items-center justify-center gap-1 rounded-lg border border-dashed px-6 outline-none" data-test={`${dataTest}-empty-add`} disabled={isAddDisabled} onClick={onAdd} type="button"><span className="text-text-link flex items-center gap-1.5 text-base"><PlusIcon aria-hidden="true" className="size-5 shrink-0" focusable="false" />{addLabel}</span></button> : <><div className="border-border-100 border-t" /><div className="[&>*+*]:border-border-100 flex flex-col gap-4 [&>*+*]:border-t [&>*+*]:pt-4">{children}</div></>;
    $[21] = addLabel;
    $[22] = children;
    $[23] = dataTest;
    $[24] = isAddDisabled;
    $[25] = isEmpty;
    $[26] = isLoading;
    $[27] = onAdd;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  let t9;
  if ($[29] !== dataTest || $[30] !== t7 || $[31] !== t8) {
    t9 = <section className="border-border-100 bg-bg-primary flex min-w-0 flex-1 flex-col gap-5 rounded-xl border p-5" data-test={dataTest}>{t7}{t8}</section>;
    $[29] = dataTest;
    $[30] = t7;
    $[31] = t8;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  return t9;
}